import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { queryCreate, queryDelete, queryGet } from './fetch';
import { BillingInformation } from './quotation';
import { useCallback } from 'react';

export interface PaymentMethod {
  id: string;
  customerId: string;
  billingInformationId: string;
  organizationId: string;
  fingerprint: string;
  brand: string;
  country: string | null;
  expMonth: number;
  expYear: number;
  funding: string;
  last4: string;
  updatedAt: string;
  createdAt: string;
  allowReuse: boolean;
  sessionId?: string;
}

export interface Intent {
  client_secret: string;
  [key: string]: any;
}

export const useCreateIntent = (organizationId?: string) => {
  return useMutation<Intent, AxiosError, void>(
    queryCreate(`/v2/organizations/${organizationId}/payment-methods/intents`),
  );
};

export const useSetDefaultPaymentMethod = (organizationId?: string) => {
  const queryClient = useQueryClient();

  const onSuccess = useCallback(
    (info: BillingInformation) => {
      queryClient.setQueryData(
        ['organizations', organizationId, 'billing-informations'],
        info,
      );
      queryClient.setQueryData(['billing-informations', info.id], info);
    },
    [queryClient, organizationId],
  );
  return useMutation<BillingInformation, AxiosError, { paymentMethod: string }>(
    queryCreate(`/v2/organizations/${organizationId}/payment-methods/default`),
    { onSuccess },
  );
};

export const usePaymentMethod = (pmId?: string) => {
  return useQuery<PaymentMethod>(
    ['payment-methods', pmId],
    queryGet(`/v2/payment-methods/${pmId}`),
    {
      enabled: !!pmId,
    },
  );
};

export const usePaymentMethods = (organizationId?: string) => {
  const queryClient = useQueryClient();
  return useQuery<PaymentMethod[]>(
    ['organizations', organizationId, 'payment-methods'],
    queryGet(`/v2/organizations/${organizationId}/payment-methods`),
    {
      enabled: !!organizationId,
      onSuccess: (data) => {
        data?.forEach((pm) => {
          queryClient.setQueryData(['payment-methods', pm.id], pm);
        });
      },
    },
  );
};

interface AddPaymentMethodDto {
  paymentMethod: string;
  sessionId?: string;
  allowReuse?: boolean;
  saveAsDefault?: boolean;
}

export const useAddPaymentMethod = (organizationId?: string) => {
  const resetCache = useResetPaymentMethodCache(organizationId);
  return useMutation<PaymentMethod, AxiosError, AddPaymentMethodDto>(
    queryCreate(`/v2/organizations/${organizationId}/payment-methods`),
    { onSuccess: resetCache },
  );
};

export const useRemovePaymentMethod = (organizationId?: string) => {
  const resetCache = useResetPaymentMethodCache(organizationId);
  return useMutation<void, AxiosError, string>(
    (id: string) => queryDelete(`/v2/payment-methods/${id}`)(),
    { onSuccess: resetCache },
  );
};

export const useResetPaymentMethodCache = (organizationId?: string) => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries([
      'organizations',
      organizationId,
      'payment-methods',
    ]);
    const info = queryClient.getQueryData<BillingInformation>([
      'organizations',
      organizationId,
      'billing-informations',
    ]);
    if (info) {
      queryClient.invalidateQueries(['billing-informations', info.id]);
    }
    queryClient.invalidateQueries([
      'organizations',
      organizationId,
      'billing-informations',
    ]);

    queryClient.invalidateQueries(['payment-methods']);
  }, [organizationId]);
};
